"use client";

import { forwardRef, useEffect, useRef, useState } from "react";
import { useFormStatus } from "react-dom";
import { useTranslations, useLocale } from "next-intl";
import { sendInquiry, type InquiryResult } from "@/app/actions/inquiry";
import { formatPrice, type SupportedCurrency } from "@/lib/format";
import DatePicker from "./DatePicker";
import GuestSelector, { type Guests } from "./GuestSelector";
import { createSupabaseBrowserClient } from "@/lib/auth/client";
import RideAddons from "./addons/RideAddons";
import { realEmailOrEmpty } from "@/lib/auth/phone";
import type { AddonSelection, RideMenu } from "@/lib/addons/types";

/**
 * Optional cost breakdown — rendered as line items if passed.
 *
 * Source-agnostic: the BookingCard builds this from `breakdown.feeLines`
 * (cleaning / access / service from the per-source dispatcher) plus the
 * grouped nightly subtotal lines. No per-source `cleaningFee` /
 * `accessFeeAdults` / `serviceFee` fields anymore — the dispatcher's
 * labels already carry the right copy for each source.
 */
export interface InquiryBreakdown {
  /** Per-period nightly groups, e.g. [{ label: "EGP 8,400 × 3 (July)", amount: 25200 }] */
  nightLines: { label: string; amount: number }[];
  /** Fee-on-top rows (cleaning, access, service, …). Order preserved. */
  feeLines: { label: string; amount: number }[];
}

interface Context {
  listingTitle: string;
  listingSlug: string;
  listingWpId?: number;
  listingUrl?: string;
  pricePerNight: number | null;
  area?: string;
  checkin: string; // ISO yyyy-mm-dd
  checkout: string; // ISO yyyy-mm-dd
  nights: number;
  guests: number; // adults + children
  totalEgp: number;
  /** Display currency for every monetary value rendered in this modal.
   *  Defaults to "EGP" when omitted; "USD" for hotel-branded units. */
  currency?: SupportedCurrency;
  /** When provided, the modal shows per-line cost breakdown above the total. */
  breakdown?: InquiryBreakdown;
  /** Ride add-ons the guest picked, already priced for display (EGP). */
  rideLines?: { label: string; amount: number }[];
  /** The same add-ons as selections; this is what gets submitted. The server
   *  re-quotes them, so no price is sent. */
  rideSelections?: AddonSelection[];
  /** Rate-plan code chosen on the booking card (D-090). The server re-validates it. */
  ratePlan?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  context: Context;
  /** When provided, the "Add a ride" section is offered inside the modal too.
   *  Phone guests reach the modal from the sticky bar and never scroll to the
   *  section in the card, so this is where most of them will see it. The parent
   *  owns the selection state; omitted when the unit has no rides or the stay
   *  cannot be quoted. */
  rides?: {
    menu: RideMenu;
    nights: number;
    value: AddonSelection[];
    onChange: (next: AddonSelection[]) => void;
  };
  /** When provided, the trip-summary cells become editable. Parent (usually
   *  BookingCard) owns the state, the modal just fires updates. */
  editable?: {
    dates: { start: Date | null; end: Date | null };
    onDatesChange: (next: { start: Date | null; end: Date | null }) => void;
    guests: Guests;
    onGuestsChange: (next: Guests) => void;
    /** Largest party the unit sleeps (units.guests). Caps the guest stepper —
     *  without it the picker fell back to GuestSelector's default of 8, so a
     *  10/12-guest villa couldn't be booked for its full capacity. */
    maxGuests?: number;
    /** Optional iCal-blocked days — passed straight to DatePicker. */
    blockedDates?: string[];
    /** Minimum nights between check-in and check-out (monthly-rental units). */
    minNights?: number | null;
  };
}

function formatHumanDate(iso: string): string {
  const d = new Date(iso);
  if (isNaN(+d)) return iso;
  return d.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export default function InquiryModal({ open, onClose, context, editable, rides }: Props) {
  const t = useTranslations("inquiryModal");
  const locale = useLocale();
  const rideLines = context.rideLines ?? [];
  const ridesTotal = rideLines.reduce((a, l) => a + l.amount, 0);
  const [result, setResult] = useState<InquiryResult | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  // Inline pickers for editing dates + guests directly from inside the modal,
  // so the user doesn't have to close it and scroll the sidebar.
  const [showCal, setShowCal] = useState(false);
  const [calAnchor, setCalAnchor] = useState<"in" | "out">("in");
  const [showGuests, setShowGuests] = useState(false);
  const checkInRef = useRef<HTMLButtonElement>(null);
  const checkOutRef = useRef<HTMLButtonElement>(null);
  const guestsRef = useRef<HTMLButtonElement>(null);

  // Pre-fill name / email / phone when a signed-in user opens the modal.
  // We don't gate the form on auth (anonymous inquiries still work); this
  // just saves the signed-in guest 30 seconds of typing.
  const [prefill, setPrefill] = useState<{ name: string; email: string; phone: string }>({
    name: "", email: "", phone: "",
  });
  useEffect(() => {
    if (!open) return;
    if (
      !process.env.NEXT_PUBLIC_SUPABASE_URL ||
      !process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY
    ) return;
    let cancelled = false;
    (async () => {
      const supabase = createSupabaseBrowserClient();
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || cancelled) return;
      const { data: p } = await supabase
        .from("profiles")
        .select("full_name, phone")
        .eq("id", user.id)
        .maybeSingle<{ full_name: string | null; phone: string | null }>();
      if (cancelled) return;
      setPrefill({
        name: p?.full_name ?? "",
        // Never the internal phone-account address — it receives no mail.
        email: realEmailOrEmpty(user.email),
        phone: p?.phone ?? "",
      });
    })();
    return () => { cancelled = true; };
  }, [open]);

  // Reset result whenever the modal is reopened
  useEffect(() => {
    if (open) setResult(null);
  }, [open]);

  // Body scroll lock + Escape to close
  useEffect(() => {
    if (!open) return;
    const orig = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = orig;
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  async function handleAction(formData: FormData) {
    const r = await sendInquiry(formData);
    setResult(r);
    // Fire the moment the inquiry actually lands in the DB — this is the real
    // qualified-lead moment (name/phone/dates captured server-side),
    // independent of whether the guest goes on to tap "Open WhatsApp"
    // afterward. No PII in the params (GA4 disallows it) — business context
    // only, mirrors the whatsapp_click event's shape.
    if (r.ok) {
      const w = window as unknown as { gtag?: (...args: unknown[]) => void };
      if (typeof w.gtag === "function") {
        w.gtag("event", "inquiry_submitted", {
          listing_slug: context.listingSlug,
          area: context.area ?? undefined,
          total_egp: context.totalEgp > 0 ? context.totalEgp : undefined,
          currency: context.currency ?? "EGP",
        });
      }
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="inquiry-title"
      // h-[100dvh] (dynamic viewport) not inset-0: on mobile Chrome a fixed
      // inset-0 element is sized to the LARGEST viewport (toolbar hidden), so a
      // bottom-anchored sheet overflowed behind Chrome's address bar — the
      // header + × ended up off-screen and the panel couldn't be closed.
      className="fixed inset-x-0 top-0 z-[200] h-[100dvh] flex items-end sm:items-center justify-center"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* Backdrop — opaque enough (75% black + medium blur) that wishlist
          hearts on the similar-listing cards behind don't shine through as
          ghost dots beside the modal card. */}
      <div
        className="absolute inset-0 bg-black/75 backdrop-blur-md"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Card */}
      <div
        ref={dialogRef}
        className="relative w-full sm:max-w-[500px] bg-white sm:rounded-[20px] rounded-t-[20px] shadow-[var(--shadow-ss-xl)] flex flex-col max-h-[92dvh] overflow-hidden fade-up"
      >
        {/* Header */}
        <div className="p-5 border-b border-ss-line flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[11px] font-bold uppercase tracking-wider text-ss-blue">
              {t("requestToBook")}
            </div>
            <h2
              id="inquiry-title"
              className="h-display text-[20px] font-semibold mt-0.5 truncate"
            >
              {context.listingTitle}
            </h2>
            {context.area && (
              <div className="text-[13px] text-ss-muted truncate">
                {context.area}
              </div>
            )}
          </div>
          <button
            type="button"
            aria-label={t("close")}
            onClick={onClose}
            className="flex-none w-9 h-9 rounded-full hover:bg-ss-bg-soft text-xl leading-none cursor-pointer"
          >
            ×
          </button>
        </div>

        {result?.ok ? (
          <SuccessState onClose={onClose} email={context.listingTitle} />
        ) : (
          <form
            action={handleAction}
            className="overflow-y-auto p-5 flex flex-col gap-3.5"
          >
            {/* Trip summary — cells are tappable when `editable` is provided. */}
            <div className="bg-ss-bg-soft rounded-[12px] p-2 text-[13.5px] grid grid-cols-2 gap-1">
              <SummaryCell
                ref={checkInRef}
                label={t("checkIn")}
                value={formatHumanDate(context.checkin)}
                editable={!!editable}
                editIndicator={t("editIndicator")}
                onClick={() => {
                  if (!editable) return;
                  setCalAnchor("in");
                  setShowCal(true);
                  setShowGuests(false);
                }}
              />
              <SummaryCell
                ref={checkOutRef}
                label={t("checkOut")}
                value={formatHumanDate(context.checkout)}
                editable={!!editable}
                editIndicator={t("editIndicator")}
                onClick={() => {
                  if (!editable) return;
                  setCalAnchor("out");
                  setShowCal(true);
                  setShowGuests(false);
                }}
              />
              <SummaryCell
                label={t("nights")}
                value={String(context.nights)}
                editable={false}
                editIndicator={t("editIndicator")}
              />
              <SummaryCell
                ref={guestsRef}
                label={t("guests")}
                value={String(context.guests)}
                editable={!!editable}
                editIndicator={t("editIndicator")}
                onClick={() => {
                  if (!editable) return;
                  setShowGuests(true);
                  setShowCal(false);
                }}
              />
              {context.breakdown && (
                <div className="col-span-2 pt-2 mt-0.5 border-t border-ss-line space-y-1">
                  {context.breakdown.nightLines.map((l) => (
                    <BreakdownRow currency={context.currency} key={l.label} label={l.label} amount={l.amount} />
                  ))}
                  {context.breakdown.feeLines.map((l) => (
                    <BreakdownRow
                      currency={context.currency}
                      key={l.label}
                      label={l.label}
                      amount={l.amount}
                    />
                  ))}
                </div>
              )}
              {rideLines.length > 0 && (
                <div className="col-span-2 pt-2 mt-0.5 border-t border-ss-line space-y-1">
                  {rideLines.map((l) => (
                    <BreakdownRow currency={context.currency} key={l.label} label={l.label} amount={l.amount} />
                  ))}
                </div>
              )}
              {context.totalEgp > 0 && (
                <div className="col-span-2 pt-1.5 mt-0.5 border-t border-ss-line flex justify-between">
                  <span className="text-ss-muted">{t("estimatedTotal")}</span>
                  <span className="font-bold">
                    {formatPrice(context.totalEgp + ridesTotal, context.currency ?? "EGP")}
                  </span>
                </div>
              )}
            </div>

            {rides && (
              <RideAddons
                menu={rides.menu}
                nights={rides.nights}
                value={rides.value}
                onChange={rides.onChange}
                fmtMoney={(n) => formatPrice(n, context.currency ?? "EGP")}
                className="border border-ss-line rounded-[12px] p-3.5"
              />
            )}

            {/* Hidden listing/trip context */}
            <input type="hidden" name="listingTitle" value={context.listingTitle} />
            <input type="hidden" name="listingSlug" value={context.listingSlug} />
            {/* Guest's UI language at inquiry time, so the operator email can
                flag whether to reply in Arabic or English. */}
            <input type="hidden" name="locale" value={locale} />
            {context.listingWpId !== undefined && (
              <input type="hidden" name="listingWpId" value={String(context.listingWpId)} />
            )}
            {context.listingUrl && (
              <input type="hidden" name="listingUrl" value={context.listingUrl} />
            )}
            {context.area && <input type="hidden" name="area" value={context.area} />}
            {context.pricePerNight !== null && (
              <input
                type="hidden"
                name="pricePerNight"
                value={String(context.pricePerNight)}
              />
            )}
            <input type="hidden" name="checkin" value={context.checkin} />
            <input type="hidden" name="checkout" value={context.checkout} />
            <input type="hidden" name="nights" value={String(context.nights)} />
            <input type="hidden" name="guests" value={String(context.guests)} />
            <input
              type="hidden"
              name="totalEgp"
              value={context.totalEgp > 0 ? String(context.totalEgp) : ""}
            />
            {/* Tell the server action which currency `totalEgp` / `pricePerNight`
                are denominated in. Without this the inquiry row + email default
                to EGP and a USD-priced unit's total would render as "EGP 1,600". */}
            <input type="hidden" name="currency" value={context.currency ?? "EGP"} />
            {/* Ride add-on SELECTIONS only. totalEgp above stays stay-only; the
                server prices the rides itself from the unit's location. */}
            {context.ratePlan && <input type="hidden" name="ratePlan" value={context.ratePlan} />}
            {context.rideSelections && context.rideSelections.length > 0 && (
              <input type="hidden" name="addons" value={JSON.stringify(context.rideSelections)} />
            )}

            {/* Contact fields */}
            <Field
              label={t("yourName")}
              name="name"
              type="text"
              autoComplete="name"
              required
              placeholder="Maged Mohamed"
              defaultValue={prefill.name || undefined}
            />
            <div className="grid sm:grid-cols-2 gap-3.5">
              <Field
                label={t("email")}
                name="email"
                type="email"
                autoComplete="email"
                placeholder="you@example.com"
                defaultValue={prefill.email || undefined}
                optionalHint={t("optional")}
              />
              <Field
                label={t("phoneWhatsapp")}
                name="phone"
                type="tel"
                autoComplete="tel"
                required
                placeholder="+20 100 000 0000"
                defaultValue={prefill.phone || undefined}
              />
            </div>
            <div>
              <label
                htmlFor="inquiry-message"
                className="block text-[12.5px] font-bold uppercase tracking-wider text-ss-muted mb-1.5"
              >
                {t("anythingToKnow")} <span className="text-ss-soft normal-case font-medium tracking-normal">{t("optional")}</span>
              </label>
              <textarea
                id="inquiry-message"
                name="message"
                rows={3}
                placeholder={t("messagePlaceholder")}
                className="w-full rounded-[12px] border border-ss-line px-3.5 py-2.5 text-[14px] focus:outline-none focus:border-ss-blue focus:ring-2 focus:ring-ss-blue-50 resize-none"
              />
            </div>

            {result?.error && (
              <div className="bg-[#fdecea] text-[#a83a26] text-[13.5px] rounded-[10px] px-3.5 py-2.5">
                {result.error}
              </div>
            )}

            <SubmitButton />

            <p className="text-[12px] text-ss-muted text-center -mt-1">
              {t("replyNote")}
            </p>
          </form>
        )}
      </div>

      {/* Editable pickers — rendered above the modal (z:[220]) when active.
          Each one calls back into the parent so BookingCard's state stays
          authoritative and the breakdown recomputes correctly. */}
      {editable && showCal && (
        <DatePicker
          value={editable.dates}
          onChange={editable.onDatesChange}
          onClose={() => setShowCal(false)}
          anchorRef={calAnchor === "out" ? checkOutRef : checkInRef}
          blockedDates={editable.blockedDates}
          minNights={editable.minNights ?? null}
          locale={locale === "ar" ? "ar" : "en"}
        />
      )}
      {editable && showGuests && (
        <GuestSelector
          value={editable.guests}
          onChange={editable.onGuestsChange}
          onClose={() => setShowGuests(false)}
          anchorRef={guestsRef}
          max={editable.maxGuests}
          adultsOnly
          locale={locale === "ar" ? "ar" : "en"}
        />
      )}
    </div>
  );
}

interface SummaryCellProps {
  label: string;
  value: string;
  editable: boolean;
  editIndicator: string;
  onClick?: () => void;
}

const SummaryCell = forwardRef<HTMLButtonElement, SummaryCellProps>(
  function SummaryCell({ label, value, editable, editIndicator, onClick }, ref) {
    return (
      <button
        ref={ref}
        type="button"
        disabled={!editable}
        onClick={onClick}
        className={`text-start rounded-[10px] px-2.5 py-2 ${
          editable
            ? "bg-white border border-ss-line hover:border-ss-text cursor-pointer"
            : "bg-transparent cursor-default"
        }`}
      >
        <div className="text-[10.5px] font-bold uppercase tracking-wider text-ss-muted">
          {label}
          {editable && <span className="ms-1 text-ss-blue font-bold">{editIndicator}</span>}
        </div>
        <div className="font-semibold mt-0.5">{value}</div>
      </button>
    );
  },
);

function Field({
  label,
  name,
  type = "text",
  required,
  autoComplete,
  placeholder,
  defaultValue,
  optionalHint,
}: {
  label: string;
  name: string;
  type?: string;
  required?: boolean;
  autoComplete?: string;
  placeholder?: string;
  defaultValue?: string;
  /** Show a muted "(optional)" hint string — used for fields that aren't required
   *  but where the guest might assume they are (e.g. email). */
  optionalHint?: string;
}) {
  const id = `inquiry-${name}`;
  return (
    <div>
      <label
        htmlFor={id}
        className="block text-[12.5px] font-bold uppercase tracking-wider text-ss-muted mb-1.5"
      >
        {label} {required && <span className="text-ss-coral">*</span>}
        {optionalHint && (
          <span className="text-ss-soft normal-case font-medium tracking-normal">
            {" "}{optionalHint}
          </span>
        )}
      </label>
      <input
        id={id}
        name={name}
        type={type}
        required={required}
        autoComplete={autoComplete}
        placeholder={placeholder}
        defaultValue={defaultValue}
        key={defaultValue ?? ""}  // remount when prefill arrives so the value applies
        className="w-full rounded-[12px] border border-ss-line px-3.5 py-2.5 text-[14.5px] focus:outline-none focus:border-ss-blue focus:ring-2 focus:ring-ss-blue-50"
      />
    </div>
  );
}

function BreakdownRow({
  label,
  amount,
  currency = "EGP",
}: {
  label: string;
  amount: number;
  currency?: SupportedCurrency;
}) {
  return (
    <div className="flex justify-between text-[12.5px]">
      <span className="text-ss-muted">{label}</span>
      <span className="font-semibold text-ss-text">
        {formatPrice(amount, currency)}
      </span>
    </div>
  );
}

function SubmitButton() {
  const { pending } = useFormStatus();
  const t = useTranslations("inquiryModal");
  return (
    <button
      type="submit"
      disabled={pending}
      className="w-full bg-ss-blue text-white font-semibold py-3.5 rounded-[14px] hover:bg-ss-blue-dark transition-colors disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
    >
      {pending ? t("sending") : t("sendInquiry")}
    </button>
  );
}

function SuccessState({ onClose }: { onClose: () => void; email: string }) {
  const t = useTranslations("inquiryModal");
  return (
    <div className="p-8 text-center">
      <div className="w-16 h-16 rounded-full bg-[#e6f7ee] text-[#0f7d3a] text-3xl inline-grid place-items-center mb-4">
        ✓
      </div>
      <h3 className="h-display text-[22px] font-semibold mb-2">
        {t("successTitle")}
      </h3>
      <p className="text-[14.5px] text-ss-muted leading-relaxed max-w-sm mx-auto mb-5">
        {t("successBody")}
      </p>
      <div className="flex gap-2 justify-center flex-wrap">
        <a
          href="https://wa.me/201044414367"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 bg-[#25d366] text-white font-semibold px-5 py-2.5 rounded-full hover:bg-[#1ebe5a]"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M20.52 3.48A11.94 11.94 0 0012.04 0C5.5 0 .18 5.32.18 11.86c0 2.09.55 4.13 1.6 5.93L0 24l6.36-1.66a11.86 11.86 0 005.68 1.45h.01c6.54 0 11.86-5.32 11.86-11.86 0-3.17-1.23-6.15-3.39-8.45z" />
          </svg>
          {t("openWhatsapp")}
        </a>
        <button
          type="button"
          onClick={onClose}
          className="px-5 py-2.5 rounded-full border border-ss-line font-semibold text-ss-text hover:bg-ss-bg-soft cursor-pointer"
        >
          {t("close")}
        </button>
      </div>
    </div>
  );
}
